import qs from 'qs'
import { Bateria, EstruturaListagemResponse, InversorModuloResponse, ModuloPorRegiaoListagemResponse } from "src/components/projetos/types";
import { apiNoLoading, IDataResponse, STATUS_CODE } from "src/framework/providers/api";
import AProvider from "src/framework/providers/AProvider";
import { Componente } from 'src/components/componentes/types';

interface BuscarComponentesPorCategoria {
    inversores_id?: number[],
    categoria_componentes_id?: number[],
    regiao_id?: number[],
    categoria_inversor_id?: number,
    segmento?: string,
}

interface ListagemComponentesCategorias {
    id: number;
    nome: string;
    grupo: string;
    considerar_peso_telhado: boolean;
    ordem_listagem: number;
    created_at: string;
    updated_at: string;
}

export interface ListagemComponentesResponse extends Pick<Componente, 'id' | 'categoria_componentes_id' | 'modelo' | 'nome' | 'descricao' | 'peso' | 'tempo_garantia' | 'tempo_garantia_unidade' | 'considerar_calculo' | 'exibir_listagem' | 'comprimento' | 'largura' | 'altura' | 'created_at' | 'updated_at'> {
    nome_componente: string;
    descricao_componente: string;
    segmento: string;
    venda: string;
    exibir_popup_parceiro: boolean;
    regra_indicacao?: any[];
}

class ComponenteProvider extends AProvider {
    constructor() {
        super("/estoque/componentes");
    }

    async buscarInversoresPorModuloId(moduloId: number, filters: { regiao_id?: number[]; potenciaEmWatts?: number; categorias?: number[]; segmento?: string;} = {}) {
        if (!moduloId) return []
        const { potenciaEmWatts = undefined, regiao_id = [], segmento = "on", categorias = [] } = filters;

        const queryStringObj = {}

        if (potenciaEmWatts) {
            Object.assign(queryStringObj, { potencia: potenciaEmWatts / 1000 })
        }

        if (!!regiao_id.length) {
            Object.assign(queryStringObj, { regiao_id: regiao_id.join(',') })
        }

        if(!!segmento) {
            Object.assign(queryStringObj, { segmento: segmento })
        }

        if (!!categorias.length) {
            Object.assign(queryStringObj, { categoria_componentes_id: categorias.join(',') })
        }

        const queryString = qs.stringify(queryStringObj)

        const url = `${this.path}/inversor_modulos/${moduloId}?${queryString}`;
        const result: IDataResponse<InversorModuloResponse[]> = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarModulosPorRegiao(regioesIds: string[], potencia?: number, tipoProjeto?: string, categoriaInversor?: number, tensaoInversor?: number, tipoConexaoInversor?: number, segmento?: string) {
        if (!regioesIds.length) return []

        const queryStringObj = {}

        if (potencia) {
            Object.assign(queryStringObj, { potencia })
        }

        if (!!regioesIds.length) {
            Object.assign(queryStringObj, { regiao_id: regioesIds.join(',') })
        }

        if (!!tipoProjeto) {
            Object.assign(queryStringObj, { tipoProjeto });
        }

        if (!!categoriaInversor) {
            Object.assign(queryStringObj, { inversor_categoria_id: categoriaInversor })
        }

        if (!!tensaoInversor) {
            Object.assign(queryStringObj, { inversor_tensao: tensaoInversor });
        }

        if (!!tipoConexaoInversor) {
            Object.assign(queryStringObj, { inversor_tipo_conexao: tipoConexaoInversor });
        }

        if (!!segmento) {
            Object.assign(queryStringObj, { segmento: segmento });
        }
        
        const querys = qs.stringify(queryStringObj)
        
        let url = `${this.path}/modulos/regioes?${querys}`;
        const result: IDataResponse<ModuloPorRegiaoListagemResponse[]> = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarEstruturasPorModuloId(moduloId: number, params?: { potencia: string; regiao_id: number[], inversor?: string, segmento?: string }) {
        if (!moduloId) return []
        const { potencia, regiao_id, inversor, segmento } = params || {}

        const querys = qs.stringify({
            potencia: potencia || undefined,
            regiao_id: regiao_id ? regiao_id?.join(',') : undefined,
            inversor: inversor || undefined,
            segmento: segmento || undefined,
        })
        
        let url = `estoque/estruturas/modulos/${moduloId}?${querys}`;
        const result: IDataResponse<EstruturaListagemResponse[]> = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarComponentesPorCategoria(filters?: BuscarComponentesPorCategoria){
        const querys = qs.stringify({
            inversores_id: filters?.inversores_id?.join(','),
            categoria_componentes_id: filters?.categoria_componentes_id ? filters?.categoria_componentes_id?.join(',') : undefined,
            regiao_id: filters?.regiao_id ? filters?.regiao_id?.join(',') : undefined,
            categoria_inversor_id: filters?.categoria_inversor_id || undefined,
            segmento: filters?.segmento || undefined,
        })
        let url = `estoque/componentes/categorias?${querys}`;
        const result: IDataResponse<ListagemComponentesResponse[]> = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarCategoriasPorGrupo(grupo: string){
        const url = `/estoque/categorias_componentes?grupo=${grupo}`
        const result: IDataResponse<ListagemComponentesCategorias[]> = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarModulosPorInversores(inversorId: number){
        const url = `${this.path}/modulos_inversores/${inversorId}`;
        const result: IDataResponse<InversorModuloResponse[]> = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarComponentesPorId(componentesIds: any[]) {
        if (!componentesIds.length) return [];
        
        let url = `${this.path}?id_in=${componentesIds.join(',')}&includes=inversores_gridzero,inversores_rsd`;
        const result: IDataResponse = await apiNoLoading.get({ url })
        return result.data || []
    }

    async buscarBateriasPorRegiao(regioesIds: number[], segmento?: string, hibrido?: boolean): Promise<IDataResponse<Bateria[]>> {
        if (!regioesIds.length) {
            return {
                data: [],
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Nenhuma região informada"
            }
        }
        
        let url = `${this.path}/baterias/regioes?regiao_id=${regioesIds.join(",")}`;
        
        if (!!segmento) {
            url += `&segmento=${segmento}`;
        }

        if (hibrido !== undefined) {
            url += `&hibrido=${String(hibrido)}`;
        }
        
        const result: IDataResponse<Bateria[]> = await apiNoLoading.get({ url })
        return {
            ...result,
            data: result.data || []
        }
    }

    async buscarVinculosModuloId(moduloId: number, filters: { regiao_id?: number[]; categorias?: number[]; segmento?: string} = {}) {
        if (!moduloId) return []
        const { categorias = [], regiao_id = [], segmento = "on" } = filters;

        const queryStringObj = {}

        if (!!categorias.length) {
            Object.assign(queryStringObj, { categoria_componentes_id: categorias.join(',') })
        }

        if (!!regiao_id.length) {
            Object.assign(queryStringObj, { regiao_id: regiao_id.join(',') })
        }

        if(!!segmento) {
            Object.assign(queryStringObj, { segmento: segmento })
        }

        const queryString = qs.stringify(queryStringObj)

        const url = `${this.path}/vinculo_modulos/${moduloId}?${queryString}`;
        const result: IDataResponse<InversorModuloResponse[]> = await apiNoLoading.get({ url })
        return result.data || []
    }
}

export { ComponenteProvider }